import axios, { AxiosResponse } from 'axios'
import {
  ArquivoExportado,
  FamiliaIndicadores,
  FormatoExportacao,
  IndicadoresOperacionais,
  IndicadoresPerformance,
  IndicadoresQualidade,
  ParametrosIndicadores,
} from './_models'

const API_URL = process.env.REACT_APP_API_URL
const INDICADORES_OPERACIONAL_URL = `${API_URL}/atendimentos/indicadores/operacional`
const INDICADORES_QUALIDADE_URL = `${API_URL}/atendimentos/indicadores/qualidade`
const INDICADORES_PERFORMANCE_URL = `${API_URL}/atendimentos/indicadores/performance`

/** Prefixo do nome de arquivo de cada família, congelado nos contratos (D4a, D4b e P1). */
const PREFIXO_NOME_ARQUIVO: Record<FamiliaIndicadores, string> = {
  operacional: 'indicadores-operacionais',
  qualidade: 'indicadores-qualidade',
  performance: 'indicadores-performance',
}

/**
 * Nome determinístico fixado pelos contratos. Usado quando o backend não
 * expõe `Content-Disposition` ao navegador (`Access-Control-Expose-Headers`).
 * O terceiro parâmetro tem padrão `'operacional'`: todo chamador anterior à
 * família de qualidade continua produzindo exatamente o mesmo nome.
 */
export const montaNomeArquivoExportacao = (
  params: ParametrosIndicadores,
  formato: FormatoExportacao,
  familia: FamiliaIndicadores = 'operacional'
): string =>
  `${PREFIXO_NOME_ARQUIVO[familia]}-${params.empresa}-${params.dataInicio}-${params.dataFim}.${formato}`

/** Extrai o `filename` do `Content-Disposition`, quando o header chega ao cliente. */
export const extraiNomeArquivo = (contentDisposition?: string): string | undefined => {
  if (!contentDisposition) return undefined

  const filenameUtf8 = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition)
  if (filenameUtf8?.[1]) return decodeURIComponent(filenameUtf8[1].trim())

  const filename = /filename="?([^";]+)"?/i.exec(contentDisposition)
  return filename?.[1]?.trim()
}

/**
 * Indicadores operacionais do período. Parâmetros opcionais não preenchidos são
 * omitidos pelo axios (não vai `idUnidade=` vazio na URL).
 */
export const getIndicadoresOperacionais = (
  params: ParametrosIndicadores
): Promise<IndicadoresOperacionais> =>
  axios
    .get(INDICADORES_OPERACIONAL_URL, { params })
    .then((d: AxiosResponse<IndicadoresOperacionais>) => d.data)

/** Indicadores de qualidade do período — mesmos cinco parâmetros da família operacional. */
export const getIndicadoresQualidade = (
  params: ParametrosIndicadores
): Promise<IndicadoresQualidade> =>
  axios
    .get(INDICADORES_QUALIDADE_URL, { params })
    .then((d: AxiosResponse<IndicadoresQualidade>) => d.data)

/** Indicadores de performance do período, com comparação e série diária. */
export const getIndicadoresPerformance = (
  params: ParametrosIndicadores
): Promise<IndicadoresPerformance> =>
  axios
    .get(INDICADORES_PERFORMANCE_URL, { params })
    .then((d: AxiosResponse<IndicadoresPerformance>) => d.data)

/**
 * Exportação CSV/JSON das famílias. As rotas devolvem `ResponseEntity<byte[]>`
 * com `Content-Disposition` — não um `ArquivoDto` com base64 —, então o consumo é
 * `responseType: 'blob'` e `downloadArquivo` (crud-helper) não se aplica.
 */
const exportarIndicadores = async (
  url: string,
  params: ParametrosIndicadores,
  formato: FormatoExportacao,
  familia: FamiliaIndicadores
): Promise<ArquivoExportado> => {
  const resposta = await axios.get(`${url}/exportar`, {
    params: { ...params, formato },
    responseType: 'blob',
  })

  const contentDisposition =
    resposta.headers?.['content-disposition'] ?? resposta.headers?.['Content-Disposition']

  return {
    blob: resposta.data as Blob,
    nomeArquivo:
      extraiNomeArquivo(contentDisposition) ?? montaNomeArquivoExportacao(params, formato, familia),
  }
}

export const exportarIndicadoresOperacionais = (
  params: ParametrosIndicadores,
  formato: FormatoExportacao
): Promise<ArquivoExportado> =>
  exportarIndicadores(INDICADORES_OPERACIONAL_URL, params, formato, 'operacional')

export const exportarIndicadoresQualidade = (
  params: ParametrosIndicadores,
  formato: FormatoExportacao
): Promise<ArquivoExportado> =>
  exportarIndicadores(INDICADORES_QUALIDADE_URL, params, formato, 'qualidade')

export const exportarIndicadoresPerformance = (
  params: ParametrosIndicadores,
  formato: FormatoExportacao
): Promise<ArquivoExportado> =>
  exportarIndicadores(INDICADORES_PERFORMANCE_URL, params, formato, 'performance')
